// Výpočet ceny prenájmu podľa sezón, dĺžky pobytu, doplnkov a zľavového kódu.
// Účtuje sa každý kalendárny deň prenájmu vrátane prvého a posledného
// (piatok–nedeľa = 3 platené dni).

import config from '../config';
import { eachDayInclusive, fromKey } from './dates';

/** Vráti kľúč sezóny ('low' | 'mid' | 'high') pre daný deň. */
export function seasonForDate(dateKey) {
  const month = fromKey(dateKey).getMonth() + 1;
  for (const [key, season] of Object.entries(config.seasons)) {
    if (season.months.includes(month)) return key;
  }
  return 'low';
}

/**
 * Cena za konkrétny deň prenájmu.
 * V hlavnej sezóne stojí 2. a každý ďalší týždeň (od 8. dňa) len
 * config.highSeasonLongStayPrice namiesto plnej sadzby.
 */
export function priceForDay(dateKey, dayIndex) {
  const seasonKey = seasonForDate(dateKey);
  if (seasonKey === 'high' && dayIndex >= config.highSeasonLongStayFromDay) {
    return config.highSeasonLongStayPrice;
  }
  return config.seasons[seasonKey].price;
}

/**
 * Kompletný cenový rozpis pre vybraný termín.
 * @param {string} startKey  deň vyzdvihnutia 'YYYY-MM-DD'
 * @param {string} endKey    deň vrátenia 'YYYY-MM-DD' (tiež platený)
 * @param {string[]} extraIds  zvolené doplnky (id z config.extras)
 * @param {number} discountPercent  zľava v % (0 ak žiadna)
 * @param {number} voucherAmount  voucher – pevná suma v € odpočítaná pred percentuálnou
 *   zľavou (0 ak žiadny). Nemôže znížiť cenu pod 0 – ak je voucher vyšší ako medzisúčet,
 *   uplatní sa len do jeho výšky.
 */
export function calculateQuote(startKey, endKey, extraIds = [], discountPercent = 0, voucherAmount = 0) {
  const days = eachDayInclusive(startKey, endKey);

  // Rozpis dní zoskupený podľa sadzby (na prehľadné zobrazenie „7 × 110 €“).
  const rateGroups = [];
  let rentalTotal = 0;
  days.forEach((day, index) => {
    const rate = priceForDay(day, index);
    rentalTotal += rate;
    const last = rateGroups[rateGroups.length - 1];
    if (last && last.rate === rate) {
      last.count += 1;
    } else {
      rateGroups.push({ rate, count: 1, season: seasonForDate(day) });
    }
  });

  // Doplnky: sadzba za deň so stropom na rezerváciu.
  const extras = config.extras
    .filter((extra) => extraIds.includes(extra.id))
    .map((extra) => ({
      ...extra,
      total: Math.min(extra.perDay * days.length, extra.cap),
    }));
  const extrasTotal = extras.reduce((sum, extra) => sum + extra.total, 0);

  const subtotal = rentalTotal + extrasTotal;

  // Voucher (pevná suma v €) sa odpočíta ako prvý, nesmie však stiahnuť cenu pod 0.
  const voucherApplied = Math.min(Math.max(voucherAmount, 0), subtotal);
  const afterVoucher = subtotal - voucherApplied;

  // Percentuálna zľava sa počíta zo zvyšku po vouchri.
  const discountAmount = Math.round(afterVoucher * (discountPercent / 100));
  const total = afterVoucher - discountAmount;

  return {
    daysCount: days.length,
    rateGroups,
    rentalTotal,
    extras,
    extrasTotal,
    subtotal,
    voucherAmount: voucherApplied,
    discountPercent,
    discountAmount,
    total,
    deposit: config.deposit,
  };
}

export function formatEur(amount) {
  return `${amount.toLocaleString('sk-SK')} €`;
}
